// Simple IoC / Service Provider for Educraft
// Lightweight, named registrations by token (symbol) and constructor

export type Token<T> = string | symbol;
export type Ctor<T> = new (...args: any[]) => T;

interface Registration<T> {
  name: string;
  ctor: Ctor<T>;
}

export class ProviderBuilder {
  private registrations = new Map<Token<any>, Registration<any>[]>();

  // Register a constructor for a token with a name
  public add<T>(token: Token<T>, ctor: Ctor<T>, name: string): this {
    const list = this.registrations.get(token) ?? [];
    list.push({ name, ctor });
    this.registrations.set(token, list);
    return this;
  }

  public build(): Provider {
    // Create a shallow copy so builder can still be reused safely
    const copy = new Map<Token<any>, Registration<any>[]>();
    for (const [k, v] of this.registrations.entries()) {
      copy.set(k, v.slice());
    }
    return new Provider(copy);
  }
}

export class Provider {
  constructor(private readonly registrations: Map<Token<any>, Registration<any>[]>) {}

  // Resolve one implementation by token + name
  public getOne<T>(token: Token<T>, name: string): T {
    const list = this.registrations.get(token);
    if (!list || list.length === 0) {
      throw new Error(`No implementations registered for token`);
    }

    const reg = list.find((r) => r.name === name);
    if (!reg) {
      throw new Error(`No implementation registered for key '${name}'`);
    }

    return new reg.ctor();
  }

  // Resolve one implementation by token + name, or null if not found
  public getOneOrNull<T>(token: Token<T>, name: string): T | null {
    const list = this.registrations.get(token);
    if (!list || list.length === 0) return null;
    const reg = list.find((r) => r.name === name);
    if (!reg) return null;
    return new reg.ctor();
  }

  // Resolve all implementations for a token
  public getAll<T>(token: Token<T>): T[] {
    const list = this.registrations.get(token) ?? [];
    return list.map((r) => new r.ctor());
  }

  // Helper: check if a named registration exists
  public has<T>(token: Token<T>, name: string): boolean {
    const list = this.registrations.get(token) ?? [];
    return list.some((r) => r.name === name);
  }

  // Helper: list registered names for token
  public listNames<T>(token: Token<T>): string[] {
    const list = this.registrations.get(token) ?? [];
    return list.map((r) => r.name);
  }
}
